import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, PaginationItem, PaginationLink, Popover, PopoverBody, Row, Table } from "reactstrap";
import ItemsBloc from "./ItemsBloc";
import Swal from "sweetalert2";
import { supplierActions } from "../../sagas/supplierSlice";
import { FaEdit, FaFilePdf, FaList, FaPlus, FaTruck, FaUser } from "react-icons/fa";
import Select from 'react-select/async';
import { components } from 'react-select';
import { Spinner } from 'reactstrap';
import ReceiptNoteForm from "../Reception/ReceiptNoteForm";
import { receiptNoteActions } from "../../sagas/receiptNoteSlice";
import { invoiceSupplierActions } from "../../sagas/invoiceSupplierSlice";
import { formatPrice } from "../../helpers/utility";
import AvoirItemBloc from "./AvoirItem";

const initForm = {
    numero: '',
    supplierId: null,
    supplierBusinessName: '',
    orderDate: new Date().toISOString().slice(0, 10),
    lines: [],
    state: 'UNPAID',
    totalAmountHt: 0,
    totalAmountTva: 0,
    totalAmountTtc: 0,
    modeReglement: 'ESPECE',
    deliveryAddress: ''
};

const AvoirForm = ({ onClose, avoir }) => {
    const { t } = useTranslation();
    const [formState, setForm] = useState(initForm);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [showSupplierModal, setShowSupplierModal] = useState(false);
    const [newSupplier, setNewSupplier] = useState({ name: '', contact: '', address: '' });
    const { invoiceNumber, loading } = useSelector(state => state.invoiceSupplier);

    const loadSupplierOptions = async (inputValue) => {
        return new Promise((resolve) => {
            dispatch(supplierActions.search({
                query: inputValue,
                onSuccess: (data) => {
                    const options = data.content.map(supplier => ({
                        value: supplier.id,
                        label: supplier.businessName,
                        supplier: supplier
                    }));
                    resolve(options);
                }
            }));
        });
    };



    useEffect(() => {
        if (avoir) {
            setForm({ ...avoir });
        } else if(!avoir && !invoiceNumber) {
            dispatch(invoiceSupplierActions.fetchNewInvoiceNumber({type: 'AVOIR'}));
        }
    }, []);

    useEffect(() => {
        if (invoiceNumber) {
            setForm(prevInvoice => ({ ...prevInvoice, numero: invoiceNumber }));
        }
    }, [invoiceNumber]);


    const formik = useFormik({
        initialValues: { ...formState, numero: avoir ? formState.numero : invoiceNumber},
        enableReinitialize: true,
        onSubmit: (values) => {
            Swal.fire({
                title: t('avoir.confirmation'),
                text: t('avoir.saveOrConfirmText'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: t('common.save'),
                cancelButtonText: t('common.cancel'),

            }).then((result) => {
                if (result.isConfirmed) {
                    values.type = 'AVOIR';
                    const payload = {
                        invoice: values,
                        onSuccess: () => {
                            Swal.fire({
                                icon: 'success',
                                title: t('common.saved'),
                                text: t('purchase.savedSuccess'),
                            });
                            onClose();
                        }
                    };

                    if (avoir) {
                        dispatch(invoiceSupplierActions.updateInvoice(payload));
                    } else {
                        dispatch(invoiceSupplierActions.createInvoice(payload));
                    }
                }  else {
                    
                }
            });
        }
    });


    const handleSupplierChange = (selectedOption) => {
        formik.setFieldValue('lines', []); // Clear the lines when the supplier changes
        formik.setFieldValue('supplierId', selectedOption?.value || null);
        formik.setFieldValue('supplierBusinessName', selectedOption?.label || '');
        formik.setFieldValue('totalAmountTtc', 0);
        formik.setFieldValue('totalAmountHt', 0);
        formik.setFieldValue('totalAmountTva', 0);
    };


    const { values, getFieldProps, handleSubmit } = formik;


    const handleCreateSupplier = () => {
        setShowSupplierModal(true);
    };


    const handleSupplierSubmit = () => {
        dispatch(supplierActions.addSupplier({
            data: newSupplier,
            onSuccess: (data) => {
                setShowSupplierModal(false);
                handleSupplierChange({
                    value: data.id,
                    label: data.businessName,
                    supplier: data
                });
                Swal.fire({
                    icon: 'success',
                    title: t('common.saved'),
                    text: t('supplier.savedSuccess')
                });
            }
        }));
    };

    const getStatusBadge = (state) => {
        const statusColors = {
            'CANCELLED': 'primary',
            'PARTIALLY_PAID': 'warning',
            'PAID': 'success',
            'UNPAID': 'danger'
        };

        return (
            <Badge color={statusColors[state] || 'light'}>
                {t(`facture.${state.toLowerCase()}`)}
            </Badge>
        );
    };

   
    return (
        <Container fluid>
            {loading  && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                </div>
            )}
            <Form onSubmit={handleSubmit} onAbort={onClose}>
                <Card>
                    <CardHeader>
                        <Row className="align-items-center">
                            <Col md="12">
                                <Row>
                                    <Col md="3">
                                        <Label htmlFor="numero">{t('purchase.orderNumber')}:</Label>
                                        <Input
                                            type="text"
                                            id="numero"
                                            {...getFieldProps('numero')}
                                            disabled
                                            className="form-control"
                                        />
                                    </Col>
                                    <Col md="3">
                                        <Label htmlFor="orderDate">{t('purchase.orderDate')}:</Label>
                                        <Input
                                            type="date"
                                            id="orderDate"
                                            {...getFieldProps('orderDate')}
                                            className="form-control"
                                            disabled={values.state !== 'UNPAID'}
                                        />
                                    </Col>
                                    <Col md="4">
                                        <Label htmlFor="supplier">{t('purchase.supplier')}:</Label>
                                        <Select
                                            id="supplier"
                                            name="supplier"
                                            isClearable
                                            placeholder={t('purchase.selectSupplier')}
                                            loadOptions={loadSupplierOptions}
                                            onChange={handleSupplierChange}
                                            defaultOptions
                                            value={values.supplierId ? {
                                                value: values.supplierId,
                                                label: values.supplierBusinessName
                                            } : null}
                                            className="react-select"
                                            classNamePrefix="select"
                                            noOptionsMessage={() => t('purchase.noSupplierFound')}
                                            loadingMessage={() => t('common.loading')}
                                            components={{
                                                DropdownIndicator: () => <FaUser className="me-2" />,
                                                Option: ({ children, ...props }) => (
                                                    <components.Option {...props}>{children}</components.Option>
                                                ),
                                                MenuList: ({ children, ...props }) => (
                                                    <components.MenuList {...props}>
                                                        {children}
                                                        <div className="border-top p-2">
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                block
                                                                onClick={handleCreateSupplier}
                                                            >
                                                                <FaPlus className="me-1" /> {t('supplier.new')}
                                                            </Button>
                                                        </div>
                                                    </components.MenuList>
                                                )
                                            }}

                                        />
                                        {formik.touched.supplierId && formik.errors.supplierId && (
                                            <div className="invalid-feedback d-block">
                                                {formik.errors.supplierId}
                                            </div>
                                        )}
                                    </Col>
                                    <Col md="2">
                                        <Label htmlFor="state">{t('purchase.state.title')}:</Label>
                                        <div className="form-control-plaintext">
                                            {getStatusBadge(values.state)}
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            
                        </Row>
                    </CardHeader>
                    <CardBody>
                        <Row>
                            <AvoirItemBloc formik={formik} />
                        </Row>
                        <Row className="mt-3 justify-content-end">
                            <Col md={4}>
                                <div className="totals text-center">
                                    <p><strong>{t('cart.totalHT')}:</strong> {formatPrice(values.totalAmountHt ?? 0)}</p>
                                    <p><strong>{t('cart.totalTVA')}:</strong> {formatPrice(values.totalAmountTva ?? 0)}</p>
                                    <p><strong>{t('cart.totalTTC')}:</strong> {formatPrice(values.totalAmountTtc ?? 0)}</p>
                                </div>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <Button type="submit" color="primary" className="me-2"
                                    disabled={values.state !== 'UNPAID'}
                                >
                                    {t('common.save')}
                                </Button>
                                <Button type="button" color="secondary" onClick={onClose}>
                                    {t('common.cancel')}
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Form>
            <Modal isOpen={showSupplierModal} toggle={() => setShowSupplierModal(false)} size="xl">
                <ModalHeader toggle={() => setShowSupplierModal(false)}>
                    {t('supplier.new')}
                </ModalHeader>
                <ModalBody>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('supplier.description')}</Label>
                                <Input
                                    value={newSupplier.businessName}
                                    onChange={e => setNewSupplier({ ...newSupplier, businessName: e.target.value })}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('supplier.phone')}</Label>
                                <Input
                                    value={newSupplier.contact}
                                    onChange={e => setNewSupplier({ ...newSupplier, contact: e.target.value })}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('supplier.email')}</Label>
                                <Input
                                    type="email"
                                    value={newSupplier.email}
                                    onChange={e => setNewSupplier({ ...newSupplier, email: e.target.value })}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('facture.fournisseur.ice')}</Label>
                                <Input
                                    type="text"
                                    value={newSupplier.ice}
                                    onChange={e => setNewSupplier({ ...newSupplier, ice: e.target.value })}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label>{t('supplier.address')}</Label>
                                <Input
                                    type="textarea"
                                    value={newSupplier.address}
                                    onChange={e => setNewSupplier({ ...newSupplier, address: e.target.value })}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleSupplierSubmit}>
                        {t('common.save')}
                    </Button>
                    <Button color="secondary" onClick={() => setShowSupplierModal(false)}>
                        {t('common.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
           
        </Container>
    );
};

export default AvoirForm;